import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useCvDataExperience, useCvDataProfile } from "../locales/cv-data";
import { useSkillsChartSlides } from "../locales/skills-chart-data";
import type { Company } from "../data/cv-data";

type Lang = "ru" | "en";

type Labels = {
  summary: string;
  experience: string;
  skills: string;
  languages: string;
  languagesLine: string;
  education: string;
  presentTo: (start: string) => string;
  techPrefix: string;
  projectsPrefix: string;
  moreHere: string;
  educationLine: (year: string) => string;
};

const LABELS: Record<Lang, Labels> = {
  ru: {
    summary: "Кратко",
    experience: "Опыт работы",
    skills: "Ключевые навыки",
    languages: "Языки",
    languagesLine: "Английский — B1",
    education: "Образование",
    presentTo: (start) => `${start} — настоящее время`,
    techPrefix: "Стек",
    projectsPrefix: "Проекты",
    moreHere: "подробнее",
    educationLine: (year) =>
      `${year} · Высшее · СПбГУ, ф. ПМ-ПУ, спец. «Информационные технологии»`,
  },
  en: {
    summary: "Summary",
    experience: "Experience",
    skills: "Key skills",
    languages: "Languages",
    languagesLine: "English — B1",
    education: "Education",
    presentTo: (start) => `${start} — present`,
    techPrefix: "Tech",
    projectsPrefix: "Projects",
    moreHere: "more",
    educationLine: (year) =>
      `${year} · Saint Petersburg State University, Faculty of Applied Mathematics, Information Technologies major`,
  },
};

function mapLocaleToLang(locale: string): Lang {
  const short = locale.split("-")[0];
  return short === "ru" ? "ru" : "en";
}

function stripUrl(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

function ensureHttps(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function getGraduationYear(period: string): string {
  const parts = period.split("-").map((s) => s.trim());
  return parts[parts.length - 1] || period;
}

type HeaderLink = { href: string; label: string };

const SITE_URL_RU = "https://pvolobuev.ru";

function buildHeaderLinks(
  lang: Lang,
  profile: { email: string; githubUrl: string; linkedinUrl: string },
): HeaderLink[] {
  const email: HeaderLink = {
    href: `mailto:${profile.email}`,
    label: profile.email,
  };
  if (lang === "ru") {
    return [
      email,
      { href: SITE_URL_RU, label: stripUrl(SITE_URL_RU) },
      { href: ensureHttps(profile.githubUrl), label: stripUrl(profile.githubUrl) },
    ];
  }
  return [
    email,
    { href: ensureHttps(profile.githubUrl), label: stripUrl(profile.githubUrl) },
    { href: ensureHttps(profile.linkedinUrl), label: stripUrl(profile.linkedinUrl) },
  ];
}

function getCompanyPeriod(company: Company, labels: Labels): string {
  const positions = company.positions;
  if (!positions.length) return "";
  const firstPosition = positions[positions.length - 1];
  const latestPosition = positions[0];
  const start = firstPosition.period.split("-")[0].trim();
  const endRaw = latestPosition.period.split("-").slice(-1)[0].trim();
  const isPresent = /настоящее|present/i.test(endRaw);
  return isPresent ? labels.presentTo(start) : `${start} — ${endRaw}`;
}

function getCompanyTechStack(company: Company, max = 10): string[] {
  const latestPosition = company.positions[0];
  if (!latestPosition) return [];
  const seen = new Map<string, string>();
  for (const proj of latestPosition.projects) {
    for (const tech of proj.techStack) {
      const key = tech.toLowerCase();
      if (!seen.has(key)) seen.set(key, tech);
    }
  }
  return Array.from(seen.values()).slice(0, max);
}

function CvPrintPage() {
  const { locale } = useIntl();
  const lang = mapLocaleToLang(locale);
  const labels = LABELS[lang];

  const profile = useCvDataProfile();
  const experience = useCvDataExperience();
  const skillsSlides = useSkillsChartSlides();

  const workCompanies = useMemo(
    () => experience.filter((c) => c.id !== "education"),
    [experience],
  );
  const educationCompany = useMemo(
    () => experience.find((c) => c.id === "education"),
    [experience],
  );

  const fullName = `${profile.name} ${profile.secondName}`.trim();

  return (
    <div className="min-h-screen w-full bg-neutral-200 flex justify-center py-[8mm]">
      <article
        className="bg-white text-black shadow-md print:shadow-none"
        style={{
          width: "210mm",
          height: "297mm",
          paddingTop: "12mm",
          paddingBottom: "12mm",
          paddingLeft: "13mm",
          paddingRight: "13mm",
          fontFamily: "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
          fontSize: "10pt",
          lineHeight: 1.4,
          overflow: "hidden",
        }}
        data-name="cv-print"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-[8mm] pb-[3mm] border-b border-neutral-300">
          <div className="min-w-0">
            <h1
              className="font-bold tracking-tight text-black"
              style={{ fontSize: "22pt", lineHeight: 1.05 }}
            >
              {fullName}
            </h1>
            <p
              className="text-neutral-700 mt-[1mm]"
              style={{ fontSize: "11pt" }}
            >
              {profile.title}
            </p>
          </div>
          <div
            className="text-right text-neutral-700 shrink-0"
            style={{ fontSize: "9pt", lineHeight: 1.5 }}
          >
            {buildHeaderLinks(lang, profile).map((link) => (
              <div key={link.href}>
                <a
                  href={link.href}
                  className="text-neutral-700 hover:text-black underline-offset-2 hover:underline"
                  target={link.href.startsWith("mailto:") ? undefined : "_blank"}
                  rel={link.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                >
                  {link.label}
                </a>
              </div>
            ))}
          </div>
        </header>

        {/* Summary */}
        <section className="mt-[4mm]">
          <SectionTitle>{labels.summary}</SectionTitle>
          <p
            className="text-black mt-[1.5mm]"
            style={{ fontSize: "10pt", lineHeight: 1.4 }}
          >
            {profile.about}
          </p>
        </section>

        {/* Experience */}
        <section className="mt-[4mm]">
          <SectionTitle
            linkHref={`${SITE_URL_RU}/cv?lang=${lang}`}
            linkLabel={labels.moreHere}
          >
            {labels.experience}
          </SectionTitle>
          <div className="flex flex-col gap-[3mm] mt-[1.5mm]">
            {workCompanies.map((company) => {
              const period = getCompanyPeriod(company, labels);
              const titles = company.positions.map((p) => p.title).join(" / ");
              const achievements = company.positions[0]?.achievements.slice(0, 5) ?? [];
              const projectTitles =
                company.positions[0]?.projects
                  .map((p) => p.title)
                  .filter((title): title is string => !!title) ?? [];
              const tech = getCompanyTechStack(company);

              return (
                <div key={company.id} className="break-inside-avoid">
                  <div className="flex items-baseline justify-between gap-[4mm]">
                    <div
                      className="font-semibold text-black"
                      style={{ fontSize: "11pt" }}
                    >
                      {company.name}
                    </div>
                    <div
                      className="text-neutral-600 shrink-0"
                      style={{ fontSize: "9pt" }}
                    >
                      {period}
                    </div>
                  </div>
                  <div
                    className="text-neutral-700"
                    style={{ fontSize: "10pt" }}
                  >
                    {titles}
                  </div>
                  {achievements.length > 0 && (
                    <ul
                      className="list-disc pl-[5mm] mt-[1mm] text-black marker:text-neutral-500"
                      style={{ fontSize: "10pt", lineHeight: 1.4 }}
                    >
                      {achievements.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  )}
                  {projectTitles.length > 0 && (
                    <div
                      className="text-neutral-600 mt-[1mm]"
                      style={{ fontSize: "9pt" }}
                    >
                      <span className="font-medium">{labels.projectsPrefix}:</span>{" "}
                      {projectTitles.join(" · ")}
                    </div>
                  )}
                  {tech.length > 0 && (
                    <div
                      className="text-neutral-600 mt-[0.5mm]"
                      style={{ fontSize: "9pt" }}
                    >
                      <span className="font-medium">{labels.techPrefix}:</span>{" "}
                      {tech.join(" · ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Skills */}
        <section className="mt-[4mm]">
          <SectionTitle
            linkHref={`${SITE_URL_RU}/techradar/${lang}`}
            linkLabel={labels.moreHere}
          >
            {labels.skills}
          </SectionTitle>
          <div
            className="mt-[1.5mm] flex flex-col gap-[1mm] text-black"
            style={{ fontSize: "10pt", lineHeight: 1.4 }}
          >
            {skillsSlides.map((slide) => (
              <SkillsRow
                key={slide.headLabel}
                label={slide.headLabel.replace("\n", " ")}
                items={slide.skills.map((skill) => skill.name)}
              />
            ))}
          </div>
        </section>

        {/* Languages */}
        <section className="mt-[4mm]">
          <SectionTitle>{labels.languages}</SectionTitle>
          <p className="mt-[1.5mm] text-black" style={{ fontSize: "10pt" }}>
            {labels.languagesLine}
          </p>
        </section>

        {/* Education */}
        {educationCompany && (
          <section className="mt-[4mm]">
            <SectionTitle>{labels.education}</SectionTitle>
            <p
              className="mt-[1.5mm] text-black"
              style={{ fontSize: "10pt" }}
            >
              {labels.educationLine(
                getGraduationYear(educationCompany.positions[0]?.period ?? ""),
              )}
            </p>
          </section>
        )}
      </article>
    </div>
  );
}

function SectionTitle({
  children,
  linkHref,
  linkLabel,
}: {
  children: React.ReactNode;
  linkHref?: string;
  linkLabel?: string;
}) {
  return (
    <h2
      className="uppercase tracking-[0.15em] text-black border-b border-neutral-400 pb-[1mm] font-semibold"
      style={{ fontSize: "10pt" }}
    >
      {children}
      {linkHref && linkLabel && (
        <span
          className="normal-case tracking-normal font-normal text-neutral-500"
          style={{ fontSize: "8pt" }}
        >
          {" "}
          (
          <a
            href={linkHref}
            className="text-neutral-500 underline underline-offset-2"
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkLabel}
          </a>
          )
        </span>
      )}
    </h2>
  );
}

function SkillsRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <span className="font-semibold">{label}:</span> {items.join(" · ")}
    </div>
  );
}

export default CvPrintPage;
